// A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review.
import { geoConicEqualArea, geoAlbers } from "d3-geo";

export function geoAlbersUsaPr() {
  /** @type {any} */
  var cache,
    /** @type {any} */
    cacheStream,
    lower48 = geoAlbers(),
    /** @type {any} */
    lower48Point,
    alaska = geoConicEqualArea().rotate([154, 0]).center([-2, 58.5]).parallels([55, 65]),
    /** @type {any} */
    alaskaPoint,
    hawaii = geoConicEqualArea().rotate([157, 0]).center([-3, 19.9]).parallels([8, 18]),
    /** @type {any} */
    hawaiiPoint,
    puertoRico = geoConicEqualArea().rotate([66, 0]).center([0, 18]).parallels([8, 18]),
    /** @type {any} */
    puertoRicoPoint,
    guamMariana = geoConicEqualArea().rotate([-145, 0]).center([0, 16]).parallels([10, 20]),
    /** @type {any} */
    guamMarianaPoint,
    americanSamoa = geoConicEqualArea().rotate([170, 0]).center([0, -14]).parallels([-14, 0]),
    /** @type {any} */
    americanSamoaPoint,
    /** @type {number[] | null} */
    point,
    /** @type {any} */
    pointStream = {
      point: function (/** @type {number} */ x, /** @type {number} */ y) {
        point = [x, y];
      }
    };

  /** @param {number[]} coordinates */
  function albersUsa(coordinates) {
    var x = coordinates[0],
      y = coordinates[1];
    return (
      (point = null),
      (lower48Point.point(x, y), point) ||
        (alaskaPoint.point(x, y), point) ||
        (hawaiiPoint.point(x, y), point) ||
        (puertoRicoPoint.point(x, y), point) ||
        (guamMarianaPoint.point(x, y), point) ||
        (americanSamoaPoint.point(x, y), point)
    );
  }

  /** @param {number[]} coordinates */
  albersUsa.invert = function (coordinates) {
    var k = lower48.scale(),
      t = lower48.translate(),
      x = (coordinates[0] - t[0]) / k,
      y = (coordinates[1] - t[1]) / k;
    return /** @type {any} */ (
      y >= 0.12 && y < 0.234 && x >= -0.225 && x < -0.185
        ? alaska
        : y >= 0.166 && y < 0.234 && x >= -0.185 && x < -0.08
          ? hawaii
          : y >= 0.204 && y < 0.234 && x >= 0.3 && x < 0.38
            ? puertoRico
            : y >= 0.05 && y < 0.204 && x >= -0.415 && x < -0.225
              ? guamMariana
              : y >= 0.18 && y < 0.234 && x >= -0.415 && x < -0.225
                ? americanSamoa
                : lower48
    ).invert(coordinates);
  };

  /** @param {any} stream */
  albersUsa.stream = function (stream) {
    return cache && cacheStream === stream
      ? cache
      : (cache = multiplex([
          lower48.stream((cacheStream = stream)),
          alaska.stream(stream),
          hawaii.stream(stream),
          puertoRico.stream(stream),
          guamMariana.stream(stream),
          americanSamoa.stream(stream)
        ]));
  };

  /** @param {any} [_] */
  albersUsa.precision = function (_) {
    if (!arguments.length) return lower48.precision();
    lower48.precision(_),
      alaska.precision(_),
      hawaii.precision(_),
      puertoRico.precision(_),
      guamMariana.precision(_),
      americanSamoa.precision(_);
    return reset();
  };

  /** @param {any} [_] */
  albersUsa.scale = function (_) {
    if (!arguments.length) return lower48.scale();
    lower48.scale(_),
      alaska.scale(_ * 0.35),
      hawaii.scale(_),
      puertoRico.scale(_),
      guamMariana.scale(_),
      americanSamoa.scale(_);
    return albersUsa.translate(lower48.translate());
  };

  /** @param {any} [_] */
  albersUsa.translate = function (_) {
    if (!arguments.length) return lower48.translate();
    var k = lower48.scale(),
      x = +_[0],
      y = +_[1];

    lower48Point = lower48
      .translate(_)
      .clipExtent([
        [x - 0.455 * k, y - 0.238 * k],
        [x + 0.455 * k, y + 0.238 * k]
      ])
      .stream(pointStream);

    alaskaPoint = alaska
      .translate([x - 0.275 * k, y + 0.201 * k])
      .clipExtent([
        [x - 0.425 * k + epsilon, y + 0.12 * k + epsilon],
        [x - 0.185 * k - epsilon, y + 0.234 * k - epsilon]
      ])
      .stream(pointStream);

    hawaiiPoint = hawaii
      .translate([x - 0.18 * k, y + 0.212 * k])
      .clipExtent([
        [x - 0.185 * k + epsilon, y + 0.166 * k + epsilon],
        [x - 0.08 * k - epsilon, y + 0.234 * k - epsilon]
      ])
      .stream(pointStream);

    puertoRicoPoint = puertoRico
      .translate([x + 0.335 * k, y + 0.224 * k])
      .clipExtent([
        [x + 0.3 * k, y + 0.204 * k],
        [x + 0.38 * k, y + 0.234 * k]
      ])
      .stream(pointStream).point;

    guamMarianaPoint = guamMariana
      .translate([x - 0.415 * k, y + 0.14 * k])
      .clipExtent([
        [x - 0.45 * k, y + 0.05 * k],
        [x - 0.39 * k, y + 0.21 * k]
      ])
      .stream(pointStream).point;

    americanSamoaPoint = americanSamoa
      .translate([x - 0.415 * k, y + 0.215 * k])
      .clipExtent([
        [x - 0.45 * k, y + 0.21 * k],
        [x - 0.39 * k, y + 0.234 * k]
      ])
      .stream(pointStream).point;

    return reset();
  };

  /**
   * @param {any} extent
   * @param {any} object
   */
  albersUsa.fitExtent = function (extent, object) {
    return fitExtent(albersUsa, extent, object);
  };

  /**
   * @param {any} size
   * @param {any} object
   */
  albersUsa.fitSize = function (size, object) {
    return fitSize(albersUsa, size, object);
  };

  function reset() {
    cache = cacheStream = null;
    return albersUsa;
  }

  return albersUsa.scale(1070);
}

/**
 * @param {any[]} streams
 * @returns {any}
 */
function multiplex(streams) {
  const n = streams.length;
  return {
    /** @param {number} x @param {number} y */
    point(x, y) {
      for (const s of streams) s.point(x, y);
    },
    sphere() {
      for (const s of streams) s.sphere();
    },
    lineStart() {
      for (const s of streams) s.lineStart();
    },
    lineEnd() {
      for (const s of streams) s.lineEnd();
    },
    polygonStart() {
      for (const s of streams) s.polygonStart();
    },
    polygonEnd() {
      for (const s of streams) s.polygonEnd();
    }
  };
}

const epsilon = 1e-6;


/**
 * @param {any} projection
 * @param {any} fitBounds
 * @param {any} object
 */
function fit(projection, fitBounds, object) {
  var clip = projection.clipExtent && projection.clipExtent();
  projection.scale(150).translate([0, 0]);
  if (clip != null) projection.clipExtent(null);
  geoStream(object, projection.stream(boundsStream));
  fitBounds(boundsStream.result());
  if (clip != null) projection.clipExtent(clip);
  return projection;
}

/**
 * @param {any} projection
 * @param {any} extent
 * @param {any} object
 */
export function fitExtent(projection, extent, object) {
  return fit(projection, function(/** @type {any} */ b) {
    var w = extent[1][0] - extent[0][0],
        h = extent[1][1] - extent[0][1],
        k = Math.min(w / (b[1][0] - b[0][0]), h / (b[1][1] - b[0][1])),
        x = +extent[0][0] + (w - k * (b[1][0] + b[0][0])) / 2,
        y = +extent[0][1] + (h - k * (b[1][1] + b[0][1])) / 2;
    projection.scale(150 * k).translate([x, y]);
  }, object);
}

/**
 * @param {any} projection
 * @param {any} size
 * @param {any} object
 */
export function fitSize(projection, size, object) {
  return fitExtent(projection, [[0, 0], size], object);
}


/**
 * @param {any} geometry
 * @param {any} stream
 */
function streamGeometry(geometry, stream) {
  if (geometry && streamGeometryType.hasOwnProperty(geometry.type)) {
    /** @type {any} */ (streamGeometryType)[geometry.type](geometry, stream);
  }
}

var streamObjectType = {
  /** @param {any} object @param {any} stream */
  Feature: function(object, stream) {
    streamGeometry(object.geometry, stream);
  },
  /** @param {any} object @param {any} stream */
  FeatureCollection: function(object, stream) {
    var features = object.features, i = -1, n = features.length;
    while (++i < n) streamGeometry(features[i].geometry, stream);
  }
};

var streamGeometryType = {
  /** @param {any} object @param {any} stream */
  Sphere: function(object, stream) {
    stream.sphere();
  },
  /** @param {any} object @param {any} stream */
  Point: function(object, stream) {
    object = object.coordinates;
    stream.point(object[0], object[1], object[2]);
  },
  /** @param {any} object @param {any} stream */
  MultiPoint: function(object, stream) {
    var coordinates = object.coordinates, i = -1, n = coordinates.length;
    while (++i < n) object = coordinates[i], stream.point(object[0], object[1], object[2]);
  },
  /** @param {any} object @param {any} stream */
  LineString: function(object, stream) {
    streamLine(object.coordinates, stream, 0);
  },
  /** @param {any} object @param {any} stream */
  MultiLineString: function(object, stream) {
    var coordinates = object.coordinates, i = -1, n = coordinates.length;
    while (++i < n) streamLine(coordinates[i], stream, 0);
  },
  /** @param {any} object @param {any} stream */
  Polygon: function(object, stream) {
    streamPolygon(object.coordinates, stream);
  },
  /** @param {any} object @param {any} stream */
  MultiPolygon: function(object, stream) {
    var coordinates = object.coordinates, i = -1, n = coordinates.length;
    while (++i < n) streamPolygon(coordinates[i], stream);
  },
  /** @param {any} object @param {any} stream */
  GeometryCollection: function(object, stream) {
    var geometries = object.geometries, i = -1, n = geometries.length;
    while (++i < n) streamGeometry(geometries[i], stream);
  }
};

/**
 * @param {any} coordinates
 * @param {any} stream
 * @param {number} closed
 */
function streamLine(coordinates, stream, closed) {
  var i = -1, n = coordinates.length - closed, coordinate;
  stream.lineStart();
  while (++i < n) coordinate = coordinates[i], stream.point(coordinate[0], coordinate[1], coordinate[2]);
  stream.lineEnd();
}

/**
 * @param {any} coordinates
 * @param {any} stream
 */
function streamPolygon(coordinates, stream) {
  var i = -1, n = coordinates.length;
  stream.polygonStart();
  while (++i < n) streamLine(coordinates[i], stream, 1);
  stream.polygonEnd();
}

/**
 * @param {any} object
 * @param {any} stream
 */
function geoStream(object, stream) {
  if (object && streamObjectType.hasOwnProperty(object.type)) {
    /** @type {any} */ (streamObjectType)[object.type](object, stream);
  } else {
    streamGeometry(object, stream);
  }
}


var x0 = Infinity,
    y0 = x0,
    x1 = -x0,
    y1 = x1;

/**
 * @param {number} x
 * @param {number} y
 */
function boundsPoint(x, y) {
  if (x < x0) x0 = x;
  if (x > x1) x1 = x;
  if (y < y0) y0 = y;
  if (y > y1) y1 = y;
}


var boundsStream = {
  point: boundsPoint,
  lineStart: noop,
  lineEnd: noop,
  polygonStart: noop,
  polygonEnd: noop,
  result: function() {
    var bounds = [[x0, y0], [x1, y1]];
    x1 = y1 = -(y0 = x0 = Infinity);
    return bounds;
  }
};


function noop() {}
